/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Network, ExternalLink, Lightbulb, RefreshCw, AlertCircle } from 'lucide-react';
import api from '../services/api';

const CareerGraph = () => {
  const { user } = useAuth();
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    currentSkills: 0,
    learningGoals: 0,
    connections: 0
  });

  // Fetch user's graph data
  useEffect(() => {
    async function fetchUserGraph() {
      if (!user?.id) {
        setError('User ID not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('🕸️ Fetching graph for user:', user.id);

        // Use api.get() so Authorization: Bearer <token> is attached
        // (api.js:56-59). Previously used raw axios with no auth header
        // — the endpoint had been leaking to unauthenticated callers.
        // NOTE: api.get() returns the parsed body directly (see
        // api.js:63), so accessors are body.success / body.graph — no
        // `.data.` envelope. The previous axios shape needed .data.*;
        // if you switch away from api.get() again, adjust accordingly.
        const data = await api.get(`/kg/user-graph/${user.id}`);

        console.log('📊 API Response:', data);

        if (data.success) {
          setGraphData(data.graph);
          setStats(data.stats);
          console.log('✅ Graph loaded:', data.graph.nodes.length, 'nodes');
        } else {
          setError(data.error || 'Could not load graph');
          console.warn('⚠️ Graph fetch failed:', data.error);
        }
      } catch (error) {
        console.error('❌ Error fetching graph:', error);

        if (error.response) {
          setError(error.response.data?.error || 'Server error');
        } else if (error.request) {
          setError('Cannot connect to server. Please ensure backend is running.');
        } else {
          setError('An unexpected error occurred: ' + error.message);
        }
      } finally {
        setLoading(false);
      }
    }
    
    fetchUserGraph();
  }, [user]);

  // Neo4j Browser URL
  //const neoUrl = `http://localhost:7474/browser/?cmd=${encodeURIComponent('MATCH (n) RETURN n LIMIT 100')}`;
  const neoUrl = `https://console.neo4j.io/?cmd=${encodeURIComponent('MATCH (n) RETURN n LIMIT 100')}`;

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <Network className="w-6 h-6 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">My Career Knowledge Graph</h1>
            <p className="text-sm text-gray-600">Visual representation of your skills, learning path, and connections</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-2xl font-bold text-green-600">{stats.currentSkills}</div>
            <div className="text-xs text-gray-600">Current Skills</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-600">{stats.learningGoals}</div>
            <div className="text-xs text-gray-600">Learning Goals</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-orange-600">{stats.connections}</div>
            <div className="text-xs text-gray-600">Connections</div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-b px-6 py-3">
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-purple-600" />
            <span className="font-semibold">Legend:</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-600"></div>
            <span>You (Center)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>Current Skills</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-500"></div>
            <span>Learning Goals</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-green-500"></div>
            <span>HAS_SKILL</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-blue-500 border-dashed border-t-2"></div>
            <span>LEARNING</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-orange-500"></div>
            <span>PREREQUISITE_FOR</span>
          </div>
        </div>
      </div>

      {/* Graph Content */}
      <div className="flex-1 p-4">
        <div className="w-full h-full bg-white rounded-lg shadow-xl border-2 border-gray-200 overflow-hidden flex items-center justify-center">
          {loading && (
            <div className="text-center">
              <RefreshCw className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading your career graph...</p>
            </div>
          )}
          
          {error && !loading && (
            <div className="text-center max-w-md">
              <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <p className="text-gray-700 font-semibold mb-2">Graph Error</p>
              <p className="text-gray-600 text-sm mb-4">{error}</p>
              <p className="text-sm text-gray-500 mb-4">
                Your knowledge graph will be built automatically as you:
              </p>
              <ul className="text-left text-sm text-gray-600 space-y-2">
                <li>✓ Upload and process documents in Knowledge Base</li>
                <li>✓ Complete your learning roadmap</li>
                <li>✓ Interact with SkillBot</li>
              </ul>
            </div>
          )}
          
          {!loading && !error && graphData && (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-700 p-8">
              <Network className="w-20 h-20 mb-6 text-purple-600" />
              <h2 className="text-2xl font-bold mb-3">Graph Successfully Loaded! 🎉</h2>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6">
                <p className="text-center mb-4 text-gray-700">
                  Your knowledge graph contains:
                </p>
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-3xl font-bold text-purple-600">{graphData.nodes?.length || 0}</div>
                    <div className="text-sm text-gray-600">Total Nodes</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-blue-600">{graphData.edges?.length || 0}</div>
                    <div className="text-sm text-gray-600">Connections</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-green-600">{stats.currentSkills}</div>
                    <div className="text-sm text-gray-600">Your Skills</div>
                  </div>
                </div>
              </div>
              <p className="text-center text-gray-600 mb-4">
                3D graph visualization is coming soon! For now, explore your complete graph in Neo4j Browser.
              </p>
              <a 
                href={neoUrl}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                <ExternalLink className="w-5 h-5" />
                View Full Graph in Neo4j
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Footer Instructions */}
      <div className="bg-white border-t px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-purple-700">
          <span className="font-semibold">💡 Pro Tip:</span>
          <span>Run this in Neo4j: <code className="bg-purple-100 px-2 py-1 rounded font-mono text-xs">MATCH (n:Skill) RETURN n LIMIT 100</code></span>
        </div>
        <a 
          href={neoUrl}
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
        >
          <ExternalLink className="w-4 h-4" />
          Open Full Neo4j Browser
        </a>
      </div>
    </div>
  );
};

export default CareerGraph;